import { getDrillDownStructure } from "@client/content/shared/Consent/lib";
import { deleteEntity, isModuleEntity } from "@client/lib/layout";
import { EMPTY_OBJECT, joinDefined, toCamelCase } from "@client/lib/misc";
import { deepClone, traverseObject } from "@client/lib/object";
import {
  addVersion,
  collapseResponsiveSettings,
  compatibility,
  getInherittedSettingValue,
  isResponsiveValue,
  mergeCompositeValues,
  processPossiblyResponsiveValue,
  setResponsiveValue
} from "@client/lib/settings";
import { parseValue as parseRangeValue, parseValueInner as parseRangeValueInner } from "@client/lib/values/range";
import * as sides from "@client/lib/values/sides";
import { isZero } from "@client/lib/values/sides";
import { type ModuleSettingStructure } from "@client/settings/module";
import {
  getDefinitionInner,
  getStructureDefaultValueInner,
  getStructureElementValueInner,
  iterateStructureInner,
  type SettingsStructure,
  type SettingsStructureSetting
} from "@client/settings/structure";
import { debug } from "@common/lib/log";
import { get, identity, isArray, isFunction, isNull, isUndefined, set, values } from "lodash";

import {
  type Entities,
  EntityType,
  FieldSubType,
  FieldType,
  type GenericMap,
  type MainSettings,
  type ModuleEntity,
  type RangeSettingDefinitionOptions,
  ScreenType,
  type SettingDefinition
} from "@client/types";
import {
  baseStructure,
  commonDefinitionsAll,
  getDefaultMainSettings,
  getEntityDefaultSettings,
  getEntitySettingsStructure,
  getMainSettingsStructure,
  getModuleDefaultSettings,
  getModuleItemDefaultSettings,
  getSettingsStructure
} from "./configs/lib";

export const version = "04.00.00";

type Device = "_mobile" | "_tablet" | "_desktop";
type MaskValueResult = {
  isMask: boolean;
  result?: GenericMap<any> | null;
};

const deviceToScreen = (device: Device): ScreenType => {
  switch (device) {
    case "_desktop":
      return ScreenType.Desktop;
    case "_mobile":
      return ScreenType.Mobile;
    case "_tablet":
      return ScreenType.Tablet;
    default:
      throw new Error(`BUG. Unexpected device ${device}`);
  }
};

const clean = (obj: object | null, device: Device) => {
  if (!obj) return obj;

  traverseObject(obj, (x, k) => {
    if (isUndefined(x[k]) || (device === "_desktop" && isNull(x[k]))) {
      delete x[k];
    }
  });

  return obj;
};

type Upgrader = (obj: object, dv: any) => MaskValueResult | undefined;

const groupSettings = (
  settings: GenericMap<any> | undefined | null,
  structure: StructureWithDefinitions,
  mask: GenericMap<any>,
  device: Device
) => {
  if (!settings) return;

  const defaultValue = getStructureElementValueInner(
    baseStructure,
    commonDefinitionsAll,
    structure.structure,
    deviceToScreen(device),
    structure.definitions,
    structure.commonStructure
  );

  const processed: string[] = [];
  const process = (obj: object, m: object, k: string, path: string) => {
    const matchingKeys = Object.keys(obj).filter(
      (x) => x !== k && x.startsWith(k) && isUndefined(obj[k]) && !processed.includes(joinDefined(".", path, x))
    );

    if (!matchingKeys.length) return;

    obj[k] = matchingKeys.reduce(
      (res, x) => ({
        ...res,
        [toCamelCase(x.replace(new RegExp(`^${k}`), ""))]: obj[x]
      }),
      {}
    );

    processed.push(joinDefined(".", path, k));

    for (const key of matchingKeys) {
      delete obj[key];
    }

    let submask = m[k];
    if (isFunction(submask)) {
      const upg = submask as Upgrader;
      const dv = get(defaultValue, joinDefined(".", path, k));
      const mr = upg(obj[k], dv);

      if (mr) {
        if (mr.isMask) {
          submask = mr.result;
        } else {
          debug(`CUSTOM UPGRADE ${k} RESULT`, mr.result, "DEFAULT VALUE:", dv);
          if (isUndefined(mr.result) || (isNull(mr.result) && device === "_desktop")) {
            delete obj[k];
            return;
          }

          obj[k] = clean(mr.result, device);
          if (obj[k] && !Object.keys(obj[k]).filter((x) => !isUndefined(obj[k][x])).length) {
            delete obj[k];
          }

          return;
        }
      }
    }

    if (submask) {
      const keys = Object.keys(submask).sort((a, b) => b.localeCompare(a)); // e.g. "postInfo" should be processed BEFORE "post"
      for (const mk of keys) {
        process(obj[k], submask, mk, joinDefined(".", path, k));
      }
    }

    if (obj[k] && !Object.keys(obj[k]).filter((x) => !isUndefined(obj[k][x])).length) {
      delete obj[k];
    }
  };

  const keys = Object.keys(mask).sort((a, b) => b.localeCompare(a)); // e.g. "postInfo" should be processed BEFORE "post"
  for (const mk of keys) {
    process(settings, mask, mk, "");
  }
};

const renameSettings = (data: MainSettings, prefix: string, newPrefix: string) => {
  const keys = Object.keys(data);
  for (const key of keys) {
    if (!key.startsWith(prefix)) continue;

    const newKey = newPrefix + key.substring(prefix.length);
    data[newKey] = data[key];
    delete data[key];
  }
};

const upgradeLogo = ({ visible, ...data }: GenericMap<any>): MaskValueResult => {
  if (visible === "off") return { isMask: false };

  return {
    isMask: false,
    result: {
      ...data,
      size: data.size ?? "true|width|100|100|100|false"
    }
  };
};

const upgradeSocial = (x: GenericMap<any>): MaskValueResult => {
  if (x.onOff === "off") return { isMask: false };

  return {
    isMask: false,
    result: x
  };
};

const upgradeAnimation = (data: GenericMap<any>): MaskValueResult => {
  if (data.style === "none") return { isMask: false };

  return {
    isMask: false,
    result: data
  };
};

const upgradeBackground = (data: GenericMap<any>): MaskValueResult => {
  if (data.type === "transparent") return { isMask: false };

  return {
    isMask: false,
    result: {
      type: data.type ?? data.typeShort,
      color: data.color,
      hoverColor: data.hoverColor,
      image: data.image,
      imageSize: data.imageSize,
      imageFixed: data.imageFixed,
      imageHeight: data.imageHeight,
      imagePosition: data.imagePosition,
      imageRepeat: data.imageRepeat,
      imageWidth: data.imageWidth,
      imageParallax: data.parallax,
      overlay:
        data.overlay === "off"
          ? null
          : {
              color: data.overlayColor,
              hoverColor: data.overlayHoverColor,
              opacity: data.overlayOpacity
            },
      opacity: data.opacity,
      video: data.video,
      videoYouTubeID: data.videoYouTubeID
    }
  };
};

const upgradeBorder = ({ widthRange, ...data }: GenericMap<any>): MaskValueResult => {
  const width = widthRange ?? data.width;
  if ((!width || isZero(width)) && (!data.radius || isZero(data.radius))) return { isMask: false };

  return {
    isMask: false,
    result: {
      ...data,
      width
    }
  };
};

const upgradeBoxShadow = (data: GenericMap<any>): MaskValueResult => {
  if (data.style === "off") return { isMask: false };

  return {
    isMask: false,
    result: {
      x: data.offsetX,
      y: data.offsetY,
      blur: data.blurRadius,
      spread: data.spread,
      color: data.color
    }
  };
};

const upgradeHeading = (data: GenericMap<any>): MaskValueResult => {
  return {
    isMask: false,
    result: {
      color: data.color,
      font: data.fontName,
      letter: data.letterSpacing,
      line: data.lineHeight,
      size: data.fontSize,
      weight: data.fontWeight,

      shadow:
        !data.shadow || data.shadow.style === "off"
          ? undefined
          : {
              blur: data.shadow.blur,
              x: data.shadow.x,
              y: data.shadow.y,
              style: data.shadow.style,
              color: data.shadow.color
            }
    }
  };
};

const upgradeLabel = (data: GenericMap<any>): MaskValueResult => {
  delete data.visiblity;
  data.visibility ??= "placeholder";

  return {
    isMask: true,
    result: {
      ...BASE_MASK,
      text: upgradeText
    }
  };
};

const upgradeRedirect = (data: GenericMap<any>): MaskValueResult => {
  if (data.to === "none" || (data.to === "tab" && !data.tab) || (data.to === "url" && !data.url)) {
    return {
      isMask: false,
      result: null
    };
  }

  const { tab, ...rest } = data;
  return {
    isMask: false,
    result: {
      ...rest,
      page: tab
    }
  };
};

const upgradeText = (data: GenericMap<any>): MaskValueResult => {
  return {
    isMask: false,
    result: {
      color: data.customColor,
      linkColor: data.linkColor,
      hoverColor: data.customHoverColor,
      font: data.fontFamily,
      letter: data.letterSpacing,
      line: data.lineHeight,
      size: data.fontSize,
      style: data.fontStyle === "oblique" ? "italic" : data.fontStyle,
      weight: data.fontWeight,

      transform:
        data.transform && data.transform !== "text-transform:none"
          ? data.transform.replace("text-transform:", "").replace("font-variant:", "")
          : undefined,

      decoration:
        !data.decorationType || data.decorationType === "none"
          ? undefined
          : {
              color: data.decorationColor,
              line: data.decorationLine,
              type: data.decorationType
            },

      shadow:
        !data.shadow || data.shadow.style === "off"
          ? undefined
          : {
              blur: data.shadow.blur,
              x: data.shadow.x,
              y: data.shadow.y,
              style: data.shadow.style,
              color: data.shadow.color
            }
    }
  };
};

const BASE_MASK = {
  animation: upgradeAnimation,
  background: upgradeBackground,
  shadow: upgradeBoxShadow,
  border: upgradeBorder
};

const BUTTON_MASK = {
  ...BASE_MASK,
  text: upgradeText
};

const FIELD_MASK = {
  border: upgradeBorder,
  shadow: upgradeBoxShadow,
  text: upgradeText
};

const preupgrade = (settings: MainSettings | null) => {
  if (!settings) return;

  const keys = Object.keys(settings);
  for (const key of keys) {
    if (key.endsWith("Decoration")) {
      settings[`${key}Type`] = settings[key];
      delete settings[key];
      continue;
    }

    const newKey = key
      .replace("boxShadow", "shadow")
      .replace("BoxShadow", "Shadow")
      .replace("headingLevel", "level")
      .replace("HeadingLevel", "Level");
    if (newKey !== key) {
      settings[newKey] = settings[key];
      delete settings[key];
    }
  }

  if (settings.visible === "on") {
    delete settings.visible;
  }
};

export const upgradeSettings = (d: MainSettings, addDefaults: boolean = false): MainSettings => {
  if (d.version) return d; // 03.01.05 and below didn't have version

  const data = deepClone(d);

  data.bodyBoxShadowStyle = data.boxShadowStyle;
  data.bodyBoxShadowOffsetX = data.boxShadowOffsetX;
  data.bodyBoxShadowOffsetY = data.boxShadowOffsetY;
  data.bodyBoxShadowBlurRadius = data.boxShadowBlurRadius;
  data.bodyBoxShadowSpread = data.boxShadowSpread;
  data.bodyBoxShadowColor = data.boxShadowColor;

  delete data.boxShadowStyle;
  delete data.boxShadowOffsetX;
  delete data.boxShadowOffsetY;
  delete data.boxShadowBlurRadius;
  delete data.boxShadowSpread;
  delete data.boxShadowColor;

  preupgrade(data);

  data.textFontFamily = data.customFont;
  data.textFontSize = data.contentFontSize;
  data.textLineHeight = data.contentLineHeight;
  data.textLetterSpacing = data.contentLetterSpacing;
  data.textFontWeight = data.contentFontWeight;
  data.textCustomColor = data.contentColor;
  data.textLinkColor = data.contentLinkColor || "#2e90fa";

  delete data.customFont;
  delete data.contentFontSize;
  delete data.contentLineHeight;
  delete data.contentLetterSpacing;
  delete data.contentFontWeight;
  delete data.contentColor;
  delete data.contentLinkColor;

  data.customCss = data.switcherCustomCSS;
  delete data.switcherCustomCSS;

  renameSettings(data, "menuItem", "headerMenuItem");
  renameSettings(data, "subMenu", "headerSubmenu");
  renameSettings(data, "subMenuItem", "headerSubmenuItem");
  renameSettings(data, "fmBtn", "fmPopupButton");
  renameSettings(data, "fmLabel", "fmPopupLabel");
  renameSettings(data, "fmField", "fmPopupField");
  renameSettings(data, "fm", "floatingMenu");

  data.bodyVerticalContentAlign = data.verticalContentAlign;
  delete data.verticalContentAlign;

  data.preloaderOnOff = data.preloader; // temporary setting to detect if preloader is enabled
  delete data.preloader;
  data.preloaderWaitAllModules = data.waitAllModules;
  delete data.waitAllModules;

  // data.headerLogoSize ??= "true|width|100|100|100|false";
  // data.sidePanelLogoSize ??= "true|width|100|100|100|false";

  data.headerElements = data.headerElementsShowHide;
  delete data.headerElementsShowHide;
  data.headerMenuShowEffect = data.showMenuEffect;
  delete data.showMenuEffect;
  data.headerMenuPosition = data.menuPosition;
  delete data.menuPosition;
  data.headerMenuRootDisplay = data.mainMenuRootDisplay;
  delete data.mainMenuRootDisplay;
  data.headerMenuTrigger = data.menuOpenClickHover;
  delete data.menuOpenClickHover;
  data.headerMenuSubTitles = data.menuShowSubTitles;
  delete data.menuShowSubTitles;
  data.headerMenuVisible = data.menuVisible;
  delete data.menuVisible;

  data.headerStickyOnOff = data.headerSticky ?? "off"; // temporary setting to detect if sticky is enabled
  delete data.headerSticky;
  data.headerStickyAnimationOnOff = data.headerStickyAnimation;
  delete data.headerStickyAnimation;
  data.headerStickyCustomLogo = data.headerStickyLogo;
  delete data.headerStickyLogo;
  data.headerStickyCustomBackground = data.headerStickyBackground;
  delete data.headerStickyBackground;

  data.sidePanelElements = data.sidePanelElementsShowHide;
  delete data.sidePanelElementsShowHide;
  data.sidePanelSocialOnOff = data.sidePanelSocialOnOff === "social_show" ? "on" : "off";
  data.sidePanelSocialStyle = data.sidePanelSocialIconsStyle;
  delete data.sidePanelSocialIconsStyle;
  data.sidePanelColorType = data.sidePanelColorOptions;
  delete data.sidePanelColorOptions;

  data.sidePanelDisplay = data.sideMenuDisplay;
  delete data.sideMenuDisplay;

  delete data.sideMenuVisible; // obsolete

  for (let i = 1; i <= 9; i++) {
    data[`onePageItem${i}Title`] = data[`dotsMenuTitle${i}`];
    delete data[`dotsMenuTitle${i}`];
    data[`onePageItem${i}Type`] = data[`onePageSection${i}`];
    delete data[`onePageSection${i}`];
  }

  Object.keys(data).forEach((x) => {
    if (x.startsWith("megaMenu") || x.startsWith("customMegaMenu") || x.startsWith("mmTab")) {
      delete data[x];
    }
  });

  delete data.additionalRoleGroupsWhichCanChangeStyles;
  delete data.additionalRolesWhichCanChangeStyles;
  delete data.animationDuration;
  delete data.availableModules;
  delete data.headerOverlapChess;
  delete data.hideSpecificMainMenuTab;
  delete data.hideSpecificSidePanelTab;
  delete data.mainMenuLevelsVisible;
  delete data.mainMenuLevelsDeep;
  delete data.mediaGalleryPermissions;
  delete data.menuDeviceVisibility;
  delete data.pageSettingsAppearanceExcludedAscx;
  delete data.pageSettingsViewPermissionsExcludedRoleGroups;
  delete data.sidePanelLevelsDeep;
  delete data.sidePanelLevelsVisible;
  delete data.tabsNoContentReplacement;
  delete data.tabsNonEditable;
  delete data.tb_social_show;
  delete data.userProfileImageType;

  const l = getMainSettingsStructure();
  const mask = getMask("main", l.structure, (x, path) => {
    switch (x) {
      case "animation": {
        if (path === "header.sticky") {
          return ({ onOff, ...settings }: GenericMap<any>) => {
            if (onOff === "off") return { isMask: false };
            return {
              isMask: false,
              result: settings
            };
          };
        }
        return null;
      }
      case "social":
        return upgradeSocial;
      case "logo":
        return upgradeLogo;
      default:
        return null;
    }
  });

  let result = upgradeSettingsInner("main", {
    settings: data,
    defaultSettings: getDefaultMainSettings(),
    mask,
    structure: l,
    addDefaults
  });
  if (!getInherittedSettingValue(result, "", "floatingMenu", ScreenType.Desktop)) {
    const floatingMenu = getStructureElementValueInner(
      baseStructure,
      commonDefinitionsAll,
      l.structure["{floatingMenu}"],
      ScreenType.Desktop,
      l.definitions
    );
    result = setResponsiveValue(result, "floatingMenu", floatingMenu);
    result = setResponsiveValue(result, "floatingMenu.elements", "");
  }

  return result;
};

type StructureWithDefinitions = {
  commonStructure?: SettingsStructure;
  structure: SettingsStructure;
  definitions: GenericMap<SettingDefinition> | null;
};
export const upgradeSettingsInner = (
  target: string,
  args: {
    settings: MainSettings;
    defaultSettings: MainSettings;
    mask: GenericMap<any>;
    structure: StructureWithDefinitions;
    addDefaults: boolean;
  },
  postUpgrade: (settings: MainSettings, device: Device) => MainSettings = identity
) => {
  const rs = expandResponsiveSettings(args.settings);

  let res = Object.keys(rs).reduce((res, device: Device) => {
    debug(`\n${target}. ${device}`);

    const data = rs[device];
    const def = args.defaultSettings[device] ?? (device === "_desktop" ? args.defaultSettings : EMPTY_OBJECT);

    debug(`grouping by mask ${JSON.stringify(args.mask)}`);
    groupSettings(data, args.structure, args.mask, device);

    if (args.structure) {
      debug(`converting according to the layout`);
      const pr = adjustSettings(data, def, args.structure, device, args.addDefaults);
      res[device] = postUpgrade(pr, device);
      return res;
    }

    debug(`NO layout. Just use groupped settings.`);
    res[device] = postUpgrade(data, device);
    return res;
  }, {} as GenericMap<any>);

  delete res._desktop.$count;

  if (res._mobile.$count) {
    delete res._mobile.$count;
    res._mobile = clean(res._mobile, "_mobile");
  } else {
    delete res._mobile;
  }

  if (res._tablet.$count) {
    delete res._tablet.$count;
    res._tablet = clean(res._tablet, "_tablet");
  } else {
    delete res._tablet;
  }

  if (res._desktop) {
    res._desktop = clean(res._desktop, "_desktop");
  }

  return res._tablet || res._mobile ? res : res._desktop;
};

const expandResponsiveSettings = (settings: GenericMap<any>): GenericMap<any> => {
  const traverseObject = <T extends object>(obj: T, enter: (obj: T, key: string, path: string) => boolean) => {
    const traverse = (obj: T, path: string) => {
      Object.keys(obj).forEach((k) => {
        if (obj[k] && typeof obj[k] === "object") {
          const res = enter(obj, k, path);
          if (res) {
            traverse(obj[k], joinDefined(".", path, k));
          }
        } else {
          enter(obj, k, path);
        }
      });
    };

    traverse(obj, "");
  };

  const res: GenericMap<any> = {
    _desktop: {
      $count: 0
    },
    _tablet: {
      $count: 0
    },
    _mobile: {
      $count: 0
    }
  };

  const setValue = (path: string | undefined, name: string, value: any) => {
    if (typeof value === "object") {
      if (value && ("_mobile" in value || "_tablet" in value || "_desktop" in value)) {
        if ("_mobile" in value && !isUndefined(value._mobile)) {
          set(res._mobile, joinDefined(".", path, name), value._mobile);
          res._mobile.$count++;
        }

        if ("_tablet" in value && !isUndefined(value._tablet)) {
          set(res._tablet, joinDefined(".", path, name), value._tablet);
          res._tablet.$count++;
        }

        if ("_desktop" in value && !isUndefined(value._desktop)) {
          set(res._desktop, joinDefined(".", path, name), value._desktop);
          res._desktop.$count++;
        }
      } else if (!isUndefined(value)) {
        set(res._desktop, joinDefined(".", path, name), value);
        res._desktop.$count++;
      }
    } else {
      if (!isUndefined(value)) {
        set(res._desktop, joinDefined(".", path, name), value);
        res._desktop.$count++;
      }
    }
  };

  const getResponsiveValue = (value: any) => {
    const desktop = value.desktop;
    const tablet = value.tablet ?? desktop;
    const mobile = value.mobile ?? tablet;
    if (desktop === tablet && tablet === mobile) return desktop;

    return {
      _desktop: value.desktop,
      _tablet: value.tablet === value.desktop ? undefined : value.tablet ?? undefined,
      _mobile: value.mobile === value.tablet ? undefined : value.mobile ?? undefined
    };
  };

  traverseObject(settings, (x, k, p) => {
    if (x[k] && typeof x[k] === "object" && ("desktop" in x[k] || "tablet" in x[k] || "mobile" in x[k])) {
      setValue(p, k, getResponsiveValue(x[k]));
      return false;
    }

    setValue(p, k, x[k]);
    return true;
  });

  return res;
};

const adjustSettings = (
  settings: GenericMap<any>,
  defaultSettings: GenericMap<any>,
  structure: StructureWithDefinitions,
  device: Device,
  addDefaults: boolean = false
): GenericMap<any> => {
  const convertValue = (key: string, definition: SettingDefinition, value: any) => {
    switch (definition.type) {
      case FieldType.Checkbox: {
        if (definition.opts?.subType === FieldSubType.OnOff) {
          if (isUndefined(value) || isNull(value)) return;

          return value === true || value === "on" || value === "yes";
        }
        return !!value;
      }
      case FieldType.Sides: {
        if (!value) return;
        if (sides.isZero(value)) return "0px";

        const pv = sides.parseValue(value);
        if (pv.isLinked) return pv.linked;
        if (pv.top === pv.right && pv.right === pv.bottom && pv.bottom === pv.left) return pv.top;

        return `${pv.top} ${pv.right} ${pv.bottom} ${pv.left}`;
      }
      case FieldType.Color: {
        if (!value) return;

        return (
          value.toString().match(/^((0x){0,1}|#{0,1})([0-9A-F]{8}|[0-9A-F]{6})$/gi) && value[0] !== "#"
            ? `#${value}`
            : value.toString()
        ).toLowerCase();
      }
      case FieldType.Range: {
        if (value === 0) return value;
        if (!value) return;

        const opts = definition.opts as RangeSettingDefinitionOptions;
        let parsed = parseRangeValueInner(value.toString());
        if (!opts.units) return parsed.number;

        if (parsed.unit === "auto") return "auto";

        parsed = parseRangeValue(value.toString(), opts.units, opts.defaultUnit);
        const unit =
          parsed.unit ??
          (opts.units?.length ? (typeof opts.units[0] === "string" ? opts.units[0] : opts.units[0].value) : undefined);
        return unit ? `${parsed.number}${unit}` : parsed.number;
      }
      case FieldType.Highlight:
      case FieldType.RichEditor:
      case FieldType.Text: {
        if (value && typeof value === "object") {
          return value.normalized ?? value.original;
        }
        break;
      }
      default: {
        // NOTE: this can clash with another similar setting. This is for button side. TODO: better handling
        if (key === "size") {
          if (value === "btn-lg") return "large";
          if (value === "btn-def-size") return "medium";
          if (value === "btn-xs") return "small";
          if (value === "custom") return "custom";
        }
        break;
      }
    }
    return value;
  };

  const getDeviceValue = (v: any) => {
    if (v && typeof v === "object") {
      if ("_desktop" in v || "_tablet" in v || "_mobile" in v) return v[device];
      if ("desktop" in v || "tablet" in v || "mobile" in v) return v[device.replace("_", "")];
    }
    return v;
  };

  iterateStructureInner(
    baseStructure,
    structure.structure,
    (x) => {
      if (x.isSetting) {
        const setting: SettingsStructureSetting =
          typeof x.structure === "string"
            ? {
                def: x.structure
              }
            : (x.structure as SettingsStructureSetting);

        const definition = getDefinitionInner(commonDefinitionsAll, x.key, setting, structure.definitions);

        const fp = joinDefined(".", x.path, x.prop);

        let value = convertValue(x.prop!, definition, get(settings, fp));
        if (isUndefined(value) && addDefaults && device === "_desktop") {
          value = getDeviceValue(setting?.val ?? definition.def);
        }

        if (!isNull(value) || device !== "_desktop") {
          set(settings, joinDefined(".", x.path, x.prop!), value);
        }

        return false;
      }

      // debug("PATH", x.path, x.prop);
      // if (x.key.startsWith("[") || x.key.startsWith(">") || x.key.startsWith("<") || x.key.startsWith("{")) return true;

      const fullPath = joinDefined(".", x.path, x.prop);
      const sv = fullPath ? get(settings, fullPath) : settings;

      if (!sv) {
        if (!x.key.startsWith("(") && !x.key.startsWith("+")) {
          // mandatory composite
          const dv = get(defaultSettings, fullPath);
          if (dv) {
            set(settings, fullPath, dv);
          }
        }
        return false;
      }
      return true;
    },
    structure.commonStructure
  );

  return settings;
};

const UPGRADERS = {
  ...BASE_MASK,
  button: (x) => {
    x.radius = x.borderRadius;
    delete x.borderRadius;

    return {
      isMask: true,
      result: BUTTON_MASK
    };
  },
  h1: upgradeHeading,
  h2: upgradeHeading,
  h3: upgradeHeading,
  h4: upgradeHeading,
  h5: upgradeHeading,
  h6: upgradeHeading,
  field: (x) => {
    x.radius = x.borderRadius;
    delete x.borderRadius;

    return {
      isMask: true,
      result: FIELD_MASK
    };
  },
  label: upgradeLabel,
  redirect: upgradeRedirect,
  text: upgradeText
};
const maskCache: GenericMap<any> = {};
const getMask = (
  cacheKey: string,
  structure: SettingsStructure,
  getUpgrader?: (key: string, path: string) => Upgrader | null,
  cl?: GenericMap<any>
): GenericMap<any> => {
  if (!(cacheKey in maskCache)) {
    try {
      const mask: GenericMap<any> = {};
      iterateStructureInner(
        baseStructure,
        structure,
        (x) => {
          if (x.isSetting) return false;

          if (x.prop) {
            const upgrader = (getUpgrader ? getUpgrader(x.prop, x.path) : null) ?? UPGRADERS[x.prop];
            set(
              mask,
              joinDefined(".", x.path, x.prop),
              upgrader ??
                ((settings) => {
                  if (settings.onOff === "off") return { isMask: false };
                  delete settings.onOff;
                })
            );
            return !upgrader;
          }

          return true;
        },
        cl
      );
      maskCache[cacheKey] = mask;
    } catch (e) {
      throw new Error(`Failed to create mask for ${cacheKey}: ${e.message}`);
    }
  }
  return maskCache[cacheKey];
};

type PreupgradeInfo = {
  mask: object;
  settings: MainSettings;
};
export const preupgradeEntity = (settings: MainSettings, type: EntityType): PreupgradeInfo | undefined => {
  if (!settings) return;

  preupgrade(settings);

  if (type !== EntityType.Module && type !== EntityType.ModuleItem && type !== EntityType.Popup) {
    delete settings.animationDuration;
  }

  switch (type) {
    case EntityType.Column: {
      settings.minHeight ??= "0vh";
      break;
    }
    case EntityType.Row: {
      settings.gap ??= "0px";
      settings.stackChildren ??= false;
      break;
    }
    case EntityType.Popup:
      settings.closeButtonOnOff = settings.showCloseButton;
      delete settings.showCloseButton;
      renameSettings(settings, "cb", "closeButton");

      settings.behaviour = settings.behaviour || "over";
      settings.pushPosition = settings.pushPosition || "top";
      settings.triggerFrequency ??= settings.frequency || "1m";

      if (settings.outerWidth) {
        settings.outerWidth = compatibility.processValue(settings.outerWidth, (x) =>
          x ? x.toString().replace("%", "vw") : undefined
        );
      }
      break;
  }

  return {
    mask: getMask(type, getEntitySettingsStructure(type).structure),
    settings
  };
};

const preupgradeModuleOrItem = (settings: MainSettings | null) => {
  if (!settings) return;

  preupgrade(settings);

  if (settings.labelVisibility) {
    settings.labelIsSmall = settings.labelVisibility === "small-label";
    delete settings.labelVisibility;
  }

  renameSettings(settings, "redirectTo", "redirect");
  if (settings.redirect) {
    settings.redirectTo = settings.redirect;
    delete settings.redirect;
  }

  const keys = Object.keys(settings);
  // adjust text settings
  const adjustHeading = (level: number) => {
    const replace = (a: string, b: string) => {
      const matching = keys.filter((x) => x.includes(a));
      for (const key of matching) {
        const newKey = key.replace(a, b);
        settings[newKey] = settings[key];
        delete settings[key];
      }
    };

    // titleFontFamilyH1
    replace(`titleFontFamilyH${level}`, `h${level}FontName`);
    replace(`titleFontSizeH${level}`, `h${level}FontSize`);
    replace(`titleLineHeightH${level}`, `h${level}LineHeight`);
    replace(`titleLetterSpacingH${level}`, `h${level}LetterSpacing`);
    replace(`titleFontWeightH${level}`, `h${level}FontWeight`);
    replace(`titleCustomColorH${level}`, `h${level}Color`);

    replace(`titleShadowColorH${level}`, `h${level}ShadowColor`);
    replace(`titleShadowStyleH${level}`, `h${level}ShadowStyle`);
    replace(`titleHShadowH${level}`, `h${level}ShadowX`);
    replace(`titleVShadowH${level}`, `h${level}ShadowY`);
    replace(`titleBlurRadiusH${level}`, `h${level}ShadowBlur`);
  };
  adjustHeading(1);
  adjustHeading(2);
  adjustHeading(3);
  adjustHeading(4);
  adjustHeading(5);
  adjustHeading(6);
};

export const preupgradeModule = (settings: MainSettings, type: string): PreupgradeInfo => {
  switch (type) {
    case "ContactForm": {
      if (settings.buttonCaption) {
        delete settings.btnCaption;
      }
      break;
    }
  }

  preupgradeModuleOrItem(settings);

  renameSettings(settings, "sbtn", "secondaryButton");
  renameSettings(settings, "btn", "button");

  const mi = getSettingsStructureInner(type);

  if (!mi.settingsStructure) throw new Error(`Module ${type} has no layout`);

  if (settings.labelPlacement) {
    settings.labelVisiblity ??= "no-label";
  }

  settings.containerWidth ??= "100%";
  settings.horizontalAlign ??= "center";

  switch (type) {
    case "BlogDetailComments": {
      renameSettings(settings, "mbtn", "moreButton");

      break;
    }
    case "BlogDetailHeader": {
      settings.overlayOnOff = settings.overlay;
      renameSettings(settings, "overlay", "imageOverlay");

      delete settings.imageOverlay;

      break;
    }
    case "BlogList": {
      renameSettings(settings, "button", "readMore");
      settings.readMoreRadius = settings.readMoreBorderRadius;
      delete settings.readMoreBorderRadius;

      break;
    }
    case "Button": {
      settings.gap = "0px";

      break;
    }
    case "Checkout": {
      settings.eventOnOff = settings.displayEvents;
      delete settings.displayEvents;
      settings.eventMode = settings.eventsMode;
      delete settings.eventsMode;
      settings.eventIds = settings.events;
      delete settings.events;

      const upgradeCheckoutItems = (items: any): string => {
        if (!items || typeof items !== "string") {
          return "FirstName,LastName,Email,Address,Phone,Company";
        }
        const it = items as string;
        if (!it.includes("Email")) {
          return it.split(",").concat(["Email"]).join(",");
        }
        return it;
      };

      settings.billingItems = upgradeCheckoutItems(settings.billingItems);
      settings.shippingItems = upgradeCheckoutItems(settings.shippingItems);

      renameSettings(settings, "cbtn", "couponButton");

      break;
    }
    case "Counter": {
      renameSettings(settings, "counterText", "text");
      break;
    }
    case "Events": {
      settings.overlayOnOff = settings.overlay;

      break;
    }
    case "KnowledgeBase": {
      renameSettings(settings, "knowledgeBase", "item");
      renameSettings(settings, "button", "secondaryButton");

      break;
    }
    case "Kpi": {
      settings.afterText = settings.textAfter;
      delete settings.textAfter;
      settings.beforeText = settings.textBefore;
      delete settings.textBefore;

      break;
    }
    case "Image": {
      settings.imageSrc = settings.image;
      delete settings.image;
      settings.imageAlt = settings.alt;
      delete settings.alt;
      settings.imageCorners = settings.corners;
      delete settings.corners;

      settings.imagePosition ??= "center center";

      renameSettings(settings, "border", "imageBorder");
      renameSettings(settings, "shadow", "imageShadow");

      break;
    }
    case "Lists": {
      settings.textLinkColor ??= settings.textCustomColor;
      break;
    }
    case "LoginForm": {
      settings.buttonCaption = settings.lbtnCaption;
      delete settings.lbtnCaption;
      settings.heading = settings.lHeading;
      delete settings.lheading;
      settings.hint = settings.lHint;
      delete settings.lhint;

      delete settings.llinkCaption;
      delete settings.lHeading;
      delete settings.lHint;
      delete settings.llinkCaption;
      delete settings.rExpiredHeading;
      delete settings.rExpiredHint;
      delete settings.rHeading;
      delete settings.rHint;
      delete settings.rbtnCaption;
      delete settings.sHeading;
      delete settings.sHint;
      delete settings.sSentHeading;
      delete settings.sSentHint;
      delete settings.slinkCaption;
      delete settings.secondaryButtonCaption;
      break;
    }
    case "MemberAffiliateDashboard": {
      settings.welcomeOnOff = settings.showWelcome;
      delete settings.showWelcome;

      renameSettings(settings, "reg", "welcome");
      renameSettings(settings, "te", "welcome");
      renameSettings(settings, "video", "welcomeVideo");

      settings.welcomeDesription = settings.desription;
      delete settings.desription;
      settings.welcomeTitle = settings.title;
      delete settings.title;
      break;
    }
    case "MemberHelpdesk": {
      renameSettings(settings, "ibtn", "secondaryButton");
      break;
    }
    case "MemberProfile": {
      renameSettings(settings, "pbtn", "button");
      settings.buttonInnerHorizontalAlign = "left";
      break;
    }
    case "MemberTicketDetail": {
      renameSettings(settings, "ibtn", "secondaryButton");
      break;
    }
    case "MemberWallets": {
      renameSettings(settings, "account", "item");
      break;
    }
    case "OptInFormV2": {
      renameSettings(settings, "img", "image");
      renameSettings(settings, "pbtn", "popupButton");
      renameSettings(settings, "disclaimer", "popupDisclaimer");
      settings.popupDisclaimerTitle = settings.popupDisclaimer;
      delete settings.popupDisclaimer;
      renameSettings(settings, "heading", "popupHeading");
      settings.popupHeadingTitle = settings.popupHeading;
      delete settings.popupHeading;

      settings.display ??= "page";
      break;
    }
    case "PieChart": {
      renameSettings(settings, "percentText", "text");
      break;
    }
    case "PortfolioGallery": {
      renameSettings(settings, "img", "image");
      renameSettings(settings, "description", "contentDescription");
      renameSettings(settings, "overlay", "contentOverlay");
      renameSettings(settings, "title", "contentTitle");
      settings.contentHoverBehaviour = settings.hoverBehaviour;
      delete settings.hoverBehaviour;
      break;
    }
    case "ProductDetail": {
      settings.addToCartCaption = settings.buttonAddToCartCaption ?? "Add to cart";
      delete settings.buttonAddToCartCaption;
      settings.buyNowCaption = settings.buttonBuyNowCaption ?? "Buy now";
      delete settings.buttonBuyNowCaption;
      settings.addReviewCaption = settings.buttonAddReviewCaption ?? "Add review";
      delete settings.buttonAddReviewCaption;
      settings.editReviewCaption = settings.buttonEditReviewCaption ?? "Edit review";
      delete settings.buttonEditReviewCaption;

      renameSettings(settings, "img", "image");
      renameSettings(settings, "regularPrice", "priceRegular");
      renameSettings(settings, "salePrice", "priceSale");
      settings.priceSaleOldPriceColor = settings.oldPriceColor;
      delete settings.oldPriceColor;

      break;
    }
    case "ProductList": {
      settings.addToCartCaption = settings.buttonAddToCartCaption ?? "Add to cart";
      delete settings.buttonAddToCartCaption;
      settings.seeDetailsCaption = settings.buttonSeeDetailsCaption ?? "See details";
      delete settings.buttonSeeDetailsCaption;
      settings.buyNowCaption = settings.buttonBuyNowCaption ?? "Buy now";
      delete settings.buttonBuyNowCaption;

      settings.tmpButtons = settings.buttons;
      delete settings.buttons;

      renameSettings(settings, "regularPrice", "priceRegular");
      renameSettings(settings, "salePrice", "priceSale");

      break;
    }
    case "Register": {
      settings.display ??= "page";
      break;
    }
    case "Spacer": {
      delete settings.visible;
      break;
    }
    case "SocialIcons": {
      settings.iconRadius = settings.iconBorderRadius;
      delete settings.iconBorderRadius;
      settings.iconUseBrandColor = settings.useBrandColor;
      delete settings.useBrandColor;

      break;
    }
  }

  return {
    mask: {
      ...BASE_MASK, // we removed advanced stuff in 4.0.1
      ...getMask(`module-${type}`, mi.settingsStructure, undefined, mi.commonStructure)
    },
    settings
  };
};

const postUpgradeModuleOrItem = (
  structure: StructureWithDefinitions,
  settings: MainSettings,
  device: Device
): MainSettings => {
  if (device === "_desktop") {
    const keys = ["field", "button", "secondaryButton"];

    let structures: GenericMap<any> = {};
    traverseObject(structure.structure, (obj, key, path) => {
      const x = keys.find((x) => key === `[${x}]`);
      if (x) {
        structures[x] = obj[key];
        return false;
      }
      return true;
    });

    for (const key of keys) {
      if (key in structures) {
        const lv = getStructureElementValueInner(
          baseStructure,
          commonDefinitionsAll,
          typeof structures[key] === "string" ? baseStructure[structures[key]] : structures[key],
          ScreenType.Desktop,
          null
        );
        settings[key] = mergeCompositeValues(settings[key], lv);
      }
    }
  }

  return settings;
};

export const postUpgradeModule = (
  structure: StructureWithDefinitions,
  settings: MainSettings,
  type: string,
  device: Device
): MainSettings => {
  if (device === "_desktop") {
    switch (type) {
      case "KnowledgeBase": {
        if (settings.secondaryButton.background) {
          settings.secondaryButton.background.color ??= "#ffffff";
          settings.secondaryButton.background.hoverColor ??= "#f5f5f5";
        }

        if (settings.secondaryButton.border) {
          settings.secondaryButton.border.color ??= "#e6e6e6";
          settings.secondaryButton.border.hoverColor ??= "#e6e6e6";
        }

        if (settings.secondaryButton.text.color === "#ffffff") {
          settings.secondaryButton.text.color = "#444444";
        }
        if (settings.secondaryButton.text.hoverColor === "#ffffff") {
          settings.secondaryButton.text.hoverColor = "#444444";
        }
        break;
      }
      case "Leaderboard": {
        settings.privacy = {
          redirect: {
            to: "url",
            newTab: true,
            url: "/privacy"
          }
        };
        settings.terms = {
          redirect: {
            to: "url",
            newTab: true,
            url: "/terms"
          }
        };
        break;
      }
      case "ProductList": {
        settings.buttons = settings.tmpButtons;
        delete settings.tmpButtons;
        break;
      }
    }
  }

  postUpgradeModuleOrItem(structure, settings, device);

  return settings;
};

const preUpgradeModuleItem = (settings: MainSettings, type: string, itemType?: string): PreupgradeInfo => {
  preupgradeModuleOrItem(settings);

  switch (type) {
    case "Button": {
      settings.radius = settings.borderRadius;
      delete settings.borderRadius;
      settings.backgroundOpacity ??= "100%";
      settings.textFontStyle ??= "normal";

      break;
    }
  }

  const mi = getSettingsStructureInner(type);

  return {
    mask: getMask(
      `module-${type}-item-${itemType ?? "default"}`,
      mi.itemsStructure![itemType ?? "default"],
      undefined,
      mi.commonStructure
    ),
    settings
  };
};

const upgradeLinks = (entities: Entities): Entities => {
  const keys = Object.keys(entities);

  for (const key of keys) {
    const entity = entities[key];
    if (!entity) continue; // deleted

    // const linkIds = entity["linkIds"];
    // if (linkIds) {
    //   delete entity["linkIds"];
    // }

    if (isModuleEntity(entity) && entity.moduleType === "Consent") {
      const parent = Object.values(entities).find((x) => isArray(x["linkIds"]) && x["linkIds"].includes(entity.id));
      if (parent) {
        let parentSettings = parent.settings!;
        if (!isResponsiveValue(parentSettings)) {
          parentSettings = {
            _desktop: parentSettings
          };
        }

        const children = entity.childrenIds!.map((x) => entities[x]);
        /*
            parent:
              settings: {enabled: 'on', title: 'Terms and conditions', location: 'above'}
            child:
              data: {fieldId: 'ddaa41b0-7c4c-4510-a7a6-5f15b20ef29f', isText: true}
              settings: {"label":"Text","width":"100%","required":"off","value":"off","visible":"on","consent":"off","name":"Text"}
          */

        processPossiblyResponsiveValue(entity.settings!, (es, screen) => {
          const device = `_${screen}`;
          const consentPath = joinDefined(".", device, "consent");

          if (es.enabled === "off") {
            if (screen !== ScreenType.Desktop) set(parentSettings, consentPath, null);
            return;
          }

          if (es.enabled === "on") {
            set(parentSettings, joinDefined(".", consentPath, "title"), es.title);
            set(parentSettings, joinDefined(".", consentPath, "location"), es.location);

            for (const child of children) {
              if (!child.settings) continue;

              const childSettings = isResponsiveValue(child.settings)
                ? child.settings[device]
                : screen === ScreenType.Desktop
                ? child.settings
                : null;
              if (!childSettings) continue;

              const { fieldId, isText } = child.data;
              if (!fieldId) continue;
              const itemPath = joinDefined(".", consentPath, "items", fieldId.toLowerCase());

              set(parentSettings, `${itemPath}.type`, isText ? "text" : "checkbox");
              set(parentSettings, `${itemPath}.label`, childSettings.label);
              if (!isText) {
                set(parentSettings, `${itemPath}.checked`, childSettings.checked === "on");
                set(parentSettings, `${itemPath}.required`, childSettings.required === "on");
                set(parentSettings, `${itemPath}.visible`, childSettings.visible !== "off");
                set(parentSettings, `${itemPath}.width`, childSettings.width ?? "100%");
              }
            }
          }
        });

        parent.settings = addVersion(collapseResponsiveSettings(parentSettings), version);
      }
      entities = deleteEntity(entities, key);
    }
  }

  delete entities["links"];

  Object.values(entities).forEach((x) => delete x["linkIds"]);

  // if (!verifySnapshotIntegrity(entities)) {
  //   throw new Error("Integrity issue!");
  // }

  return entities;
};

export const upgradeEntities = (entities: Entities, settings: MainSettings | null): Entities => {
  // remove old modules
  const keys = Object.keys(entities);
  for (const key of keys) {
    const entity = entities[key];
    if (entity.type === EntityType.Module) {
      const me = entity as ModuleEntity;

      if (me.moduleType === "ProgressBars") {
        entities = deleteEntity(entities, me.id);
      }
    }
  }

  values(entities).forEach((x) => {
    if (x.type == EntityType.Place || !x.settings || x.settings.version) return; // 03.01.05 and below didn't have version

    debug("ENTITY PRE UPGRADE:", deepClone(x.settings));
    if (x.type == EntityType.Module) {
      const me = x as ModuleEntity;

      const mss = getSettingsStructureInner(me.moduleType);
      const pi = preupgradeModule(x.settings, me.moduleType);
      if (!mss.settingsStructure) {
        console.warn(`Cannot upgrade ${me.moduleType} module. Layout is missing in the config`);
        return;
      }

      const structure: StructureWithDefinitions = {
        commonStructure: mss.commonStructure,
        structure: mss.settingsStructure,
        definitions: mss.definitions
      };
      const defaultSettings = getModuleDefaultSettingsInner(me.moduleType);

      x.settings = upgradeSettingsInner(
        `${me.moduleType} module`,
        {
          addDefaults: false,
          defaultSettings,
          structure: structure,
          mask: pi.mask,
          settings: pi.settings
        },
        (settings, device) => postUpgradeModule(structure, settings, me.moduleType, device)
      );
    } else if (x.type == EntityType.ModuleItem) {
      const me = entities![x.parentId!]! as ModuleEntity;

      const mss = getSettingsStructureInner(me.moduleType);
      const type = x.data?.type ?? "default";

      if (!mss.itemsStructure || !(type in mss.itemsStructure)) {
        throw new Error(`BUG. Module ${me.moduleType} doesn't have items or ${type} isn't described`);
      }

      const defaultSettings = getModuleItemDefaultSettingsInner(me.moduleType, type);

      const pi = preUpgradeModuleItem(x.settings, me.moduleType, type);
      const structure: StructureWithDefinitions = {
        commonStructure: mss.commonStructure,
        definitions: mss.definitions,
        structure: mss.itemsStructure[type]
      };

      x.settings = upgradeSettingsInner(
        `${me.moduleType} module item`,
        {
          addDefaults: false,
          defaultSettings,
          structure: structure,
          mask: pi.mask,
          settings: pi.settings
        },
        (settings, device) => postUpgradeModuleOrItem(structure, settings, device)
      );
    } else {
      const defaultSettings = getEntityDefaultSettings(x.type);
      const pi = preupgradeEntity(x.settings, x.type);
      if (pi) {
        x.settings = upgradeSettingsInner(`${x.type} entity`, {
          addDefaults: false,
          defaultSettings,
          structure: getEntitySettingsStructure(x.type),
          mask: pi.mask,
          settings: pi.settings
        });
      }
    }
  });

  return upgradeLinks(entities);
};

export const getSettingsStructureInner = (moduleType: string): ModuleSettingStructure => {
  switch (moduleType) {
    case "Consent": {
      return {
        definitions: null,
        settingsStructure: {
          title: {
            label: "Title",
            def: "text",
            val: "Terms and conditions"
          },
          location: {
            def: "consentLocation"
          }
        },
        itemsStructure: {
          default: getDrillDownStructure(true)
        }
      };
    }
    default:
      return getSettingsStructure(moduleType);
  }
};
export const getModuleDefaultSettingsInner = (moduleType: string) => {
  switch (moduleType) {
    case "Consent": {
      const data = getSettingsStructureInner(moduleType);
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        `upgrade-module-${moduleType}`,
        data.settingsStructure,
        data.definitions
      );
    }
    default:
      return getModuleDefaultSettings(moduleType);
  }
};
export const getModuleItemDefaultSettingsInner = (moduleType: string, type: string) => {
  switch (moduleType) {
    case "Consent": {
      const structure = getSettingsStructureInner(moduleType);
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        `upgrade-module-${moduleType}-${type}`,
        structure.itemsStructure![type],
        structure.definitions
      );
    }
    default:
      return getModuleItemDefaultSettings(moduleType, type);
  }
};
